// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import { useState } from "react"
import { ShieldCheck } from "lucide-react"
import { EU14_ALLERGEN_OPTIONS, type LactoseLevel } from "@bitebalance/contracts"
import { ChipMultiSelect } from "../ChipMultiSelect"
import { OptionGroup } from "../OptionGroup"
import {
  LACTOSE_CHIP,
  draftLactose,
  guardIntoleranceText,
  mergeChipSelection,
  splitChipSelection,
  structuredIntolerancesOf,
  withLactoseDraft,
} from "@/lib/constraintClarification"
import { FreeTextListOrNone } from "../FreeTextListOrNone"
import { Button } from "@/components/ui/button"
import { useI18n } from "@/i18n"
import type { AnswerDraft } from "../types"

interface Props {
  value: AnswerDraft
  onChange: (patch: AnswerDraft) => void
}

/**
 * Which half of the peanut/tree-nut pair is missing, if exactly one is
 * selected — null when both, neither, or "none" (owner-mandated safety
 * fail-safe: peanuts are a legume, tree nuts a separate botanical class,
 * but many real-world allergies cover both, so an incomplete selection is
 * worth a prompt rather than a silent gap).
 */
type NutClarifierKind = "add-peanuts" | "add-tree-nuts"

function nutClarifierKind(allergens: string[] | "none"): NutClarifierKind | null {
  if (allergens === "none") return null
  const hasPeanuts = allergens.includes("peanuts")
  const hasTreeNuts = allergens.includes("nuts")
  if (hasTreeNuts && !hasPeanuts) return "add-peanuts"
  if (hasPeanuts && !hasTreeNuts) return "add-tree-nuts"
  return null
}

/** Step 6 (wire) — EU-14 allergens/intolerances, both explicit-list-or-none. */
export function Step6Allergens({ value, onChange }: Props) {
  const { t } = useI18n()
  // Raw (possibly-undefined) values are what get rendered — an unanswered
  // field must never look like an explicit choice was already made (item
  // 30). "Committed" (defaulted-to-"none") values are only used to
  // reconstruct the OTHER field when writing a patch for the one the user
  // just touched — never for display.
  const allergensRaw = value.allergens as string[] | "none" | undefined
  const intolerancesRaw = value.intolerances as string[] | "none" | undefined
  const allergens = allergensRaw ?? "none"
  const intolerances = intolerancesRaw ?? "none"
  // Dismissal is per-selection, not permanent: any further chip change
  // clears it, so the notice re-appears if the selection moves back into
  // the ambiguous one-of-two state (task requirement).
  const [clarifierDismissed, setClarifierDismissed] = useState(false)
  // "Laktose" typed into the free-text box is redirected to the chip (the level is the user's choice)
  const [lactoseTextHint, setLactoseTextHint] = useState(false)
  // ADR-080: "Lactose intolerance" is its OWN choice in the same list — never the
  // milk allergy. It carries a level the user picks; until then the wire schema
  // rejects the answer and Continue stays disabled (never a guessed level).
  const structured = structuredIntolerancesOf(value.structuredIntolerances)
  const lactose = draftLactose(structured)

  const items = [
    ...EU14_ALLERGEN_OPTIONS.map(a => ({
      value: a.value,
      label: t(`onboarding.step6.allergens.${a.labelKey}`),
    })),
    { value: LACTOSE_CHIP, label: t("onboarding.step6.lactoseChip") },
  ]

  function handleAllergensChange(next: string[] | "none") {
    setClarifierDismissed(false)
    const split = splitChipSelection(next)
    onChange({
      allergens: split.allergens,
      intolerances,
      structuredIntolerances: withLactoseDraft(
        structured,
        split.lactoseSelected,
        split.lactoseSelected ? lactose.level : null
      ),
    })
  }

  function handleLactoseLevel(level: LactoseLevel) {
    onChange({
      allergens,
      intolerances,
      structuredIntolerances: withLactoseDraft(structured, true, level),
    })
  }

  const clarifierKind = nutClarifierKind(allergens)
  const showClarifier = clarifierKind !== null && !clarifierDismissed

  function addMissingNut() {
    if (allergens === "none" || clarifierKind === null) return
    const missing = clarifierKind === "add-peanuts" ? "peanuts" : "nuts"
    onChange({
      allergens: [...allergens, missing],
      intolerances,
      structuredIntolerances: structured,
    })
  }

  return (
    <div className="flex flex-col gap-6">
      <ChipMultiSelect
        legend={t("onboarding.step6.title")}
        items={items}
        value={mergeChipSelection(allergensRaw, lactose.selected)}
        onChange={handleAllergensChange}
        noneLabel={t("onboarding.step6.noneLabel")}
      />
      {lactose.selected && (
        <div
          className="flex flex-col gap-3 rounded-bb-md border border-line bg-cream/40 p-4"
          data-testid="lactose-level-card"
        >
          <p className="font-semibold text-ink">{t("onboarding.step6.lactoseLevelTitle")}</p>
          <OptionGroup
            legend={t("onboarding.step6.lactoseLevelTitle")}
            options={[
              {
                value: "avoid_all_dairy",
                label: t("onboarding.step6.lactoseLevelAvoidAll"),
                sublabel: t("onboarding.step6.lactoseLevelAvoidAllSub"),
              },
              {
                value: "lactose_free_ok",
                label: t("onboarding.step6.lactoseLevelFreeOk"),
                sublabel: t("onboarding.step6.lactoseLevelFreeOkSub"),
              },
            ]}
            value={lactose.level}
            onChange={v => handleLactoseLevel(v as LactoseLevel)}
          />
          <p className="text-xs text-ink-2">{t("onboarding.step6.lactoseLevelNote")}</p>
        </div>
      )}
      {showClarifier && (
        <div
          role="note"
          className="rounded-bb-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
        >
          <p>
            {t(
              clarifierKind === "add-peanuts"
                ? "onboarding.step6.nutClarifierPeanuts"
                : "onboarding.step6.nutClarifierTreeNuts"
            )}
          </p>
          <div className="mt-3 flex gap-2">
            <Button type="button" size="sm" className="min-h-11" onClick={addMissingNut}>
              {t("onboarding.step6.nutClarifierAdd")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="min-h-11"
              onClick={() => setClarifierDismissed(true)}
            >
              {t("onboarding.step6.nutClarifierKeep")}
            </Button>
          </div>
        </div>
      )}
      <FreeTextListOrNone
        inputId="ob-intolerances"
        label={t("onboarding.step6.intolerancesLabel")}
        placeholder={t("onboarding.step6.intolerancesPlaceholder")}
        noneLabel={t("onboarding.step6.noneLabel")}
        value={intolerancesRaw}
        onChange={next => {
          const guarded = guardIntoleranceText(intolerancesRaw, next)
          setLactoseTextHint(guarded.rejected)
          onChange({ allergens, intolerances: guarded.value, structuredIntolerances: structured })
        }}
      />
      {lactoseTextHint && (
        <p
          role="note"
          className="rounded-bb-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          data-testid="lactose-text-hint"
        >
          {t("onboarding.step6.lactoseTextHint")}
        </p>
      )}
      {/* Item 33: a dignified, legally-protective safety notice — icon +
          calmer coral/gold border treatment instead of a jarring
          destructive-red box, mirroring the recipe page's sourced-data +
          user-responsibility framing (views.recipe.allergenResponsibility).
          The underlying hard-constraint enforcement is unchanged; this is a
          presentation/tone pass only. */}
      <div className="flex gap-3 rounded-bb-lg border border-coral/30 bg-coral/5 p-4">
        <ShieldCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-danger" />
        <div className="flex flex-col gap-1 text-sm">
          <p className="font-semibold text-ink">{t("onboarding.step6.hardNoteTitle")}</p>
          <p className="text-ink-2">{t("onboarding.step6.hardNoteBody")}</p>
          <p className="text-xs text-ink-2 italic">{t("onboarding.step6.hardNoteWhy")}</p>
        </div>
      </div>
    </div>
  )
}
